import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { useEffect } from "react";
import { Toaster } from "@/components/ui/sonner";
import Layout from "@/components/Layout";
import HomePage from "@/pages/HomePage";
import ProductsPage from "@/pages/ProductsPage";
import CustomizerPage from "@/pages/CustomizerPage";
import BayWindowCustomizerPage from "@/pages/BayWindowCustomizerPage";
import PremiumBenchSeatPage from "@/pages/PremiumBenchSeatPage";
import SquareBenchSeatPage from "@/pages/SquareBenchSeatPage";
import RoundCushionCustomizerPage from "@/pages/RoundCushionCustomizerPage";
import WedgeBackCustomizerPage from "@/pages/WedgeBackCustomizerPage";
import RoundedBackSeatPage from "@/pages/RoundedBackSeatPage";
import ChairCushionCustomizerPage from "@/pages/ChairCushionCustomizerPage";
import RoundBackSeatCushionCustomizerPage from "@/pages/RoundBackSeatCushionCustomizerPage";
import TCushionCustomizerPage from "@/pages/TCushionCustomizerPage";
import LCushionCustomizerPage from "@/pages/LCushionCustomizerPage";
import WaterfallCushionPage from "@/pages/WaterfallCushionPage";
import WaterfallBackCushionPage from "@/pages/WaterfallBackCushionPage";
import ScatterCushionPage from "@/pages/ScatterCushionPage";
import BolsterCushionPage from "@/pages/BolsterCushionPage";
import OutdoorBenchSeatPage from "@/pages/OutdoorBenchSeatPage";
import HingedCushionPage from "@/pages/HingedCushionPage";
import CartPage from "@/pages/CartPage";
import CheckoutSuccessPage from "@/pages/CheckoutSuccessPage";
import FabricSamplesPage from "@/pages/FabricSamplesPage";
import OurFoamsPage from "@/pages/OurFoamsPage";
import FAQPage from "@/pages/FAQPage";
import TradePage from "@/pages/TradePage";
import { CartProvider } from "@/context/CartContext";
import { AuthProvider } from "@/context/AuthContext";
import SiteGate from "@/components/SiteGate";
import LoginPage from "@/pages/LoginPage";
import AccountPage from "@/pages/AccountPage";
import CheckoutPage from "@/pages/CheckoutPage";
import OrderTrackingPage from "@/pages/OrderTrackingPage";
import OrderDetailPage from "@/pages/OrderDetailPage";
import AdminPage from "@/pages/AdminPage";
import ForgotPasswordPage from "@/pages/ForgotPasswordPage";
import ResetPasswordPage from "@/pages/ResetPasswordPage";
import AuthCallback from "@/components/AuthCallback";
import GoogleAuthCallback from "@/components/GoogleAuthCallback";

// Component to scroll to top on route change
function ScrollToTop() {
  const { pathname } = useLocation();
  
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  
  return null;
}

import { preloadImages } from "@/utils/imageCache";

// Preload product type images on app start
['/product-type-cover-and-foam.png', '/product-type-cover-only.png', '/product-type-foam-only.png'].forEach(src => {
  const img = new Image();
  img.src = src;
});

// Preload and persistently cache all product thumbnails (survives page refresh)
const THUMBNAIL_URLS = [
  '/api/thumbnails/bay-window-bench-seat-cushion.png', '/api/thumbnails/rectangle-bench-seat-cushion.png', '/api/thumbnails/round-bench-seat-cushion.png',
  '/api/thumbnails/wedge-back-cushion.png', '/api/thumbnails/rounded-corners-bench-seat-cushion.png', '/api/thumbnails/chair-bench-seat-cushion.png',
  '/api/thumbnails/round-back-chair-seat-cushion.png', '/api/thumbnails/t-shaped-bench-seat-cushion.png', '/api/thumbnails/l-shaped-bench-seat-cushion.png',
  '/api/thumbnails/waterfall-edge-bench-seat-cushion.png', '/api/thumbnails/waterfall-edge-back-cushion.png', '/api/thumbnails/scatter-cushion.png',
  '/api/thumbnails/bolster-cushion.png',
  '/api/thumbnails/outdoor-rectangle-bench-seat-cushion.png',
  '/api/thumbnails/hinged-chair-cushion.png',
];
preloadImages(THUMBNAIL_URLS);

function App() {
  return (
    <AuthProvider>
    <SiteGate>
    <CartProvider>
      <BrowserRouter>
        <ScrollToTop />
        <Layout>
          <Routes>
            <Route path="/" element={<HomePage />} />
            <Route path="/products" element={<ProductsPage />} />
            <Route path="/products/:category" element={<ProductsPage />} />
            <Route path="/customise/bay-window-bench-seat-cushion" element={<BayWindowCustomizerPage />} />
            <Route path="/customise/rectangle-bench-seat-cushion" element={<PremiumBenchSeatPage />} />
            <Route path="/customise/square-bench-seat-cushion" element={<SquareBenchSeatPage />} />
            <Route path="/customise/round-bench-seat-cushion" element={<RoundCushionCustomizerPage />} />
            <Route path="/customise/wedge-back-cushion" element={<WedgeBackCustomizerPage />} />
            <Route path="/customise/rounded-corners-bench-seat-cushion" element={<RoundedBackSeatPage />} />
            <Route path="/customise/chair-bench-seat-cushion" element={<ChairCushionCustomizerPage />} />
            <Route path="/customise/round-back-chair-seat-cushion" element={<RoundBackSeatCushionCustomizerPage />} />
            <Route path="/customise/t-shaped-bench-seat-cushion" element={<TCushionCustomizerPage />} />
            <Route path="/customise/l-shaped-bench-seat-cushion" element={<LCushionCustomizerPage />} />
            <Route path="/customise/waterfall-edge-bench-seat-cushion" element={<WaterfallCushionPage />} />
            <Route path="/customise/waterfall-edge-back-cushion" element={<WaterfallBackCushionPage />} />
            <Route path="/customise/scatter-cushion" element={<ScatterCushionPage />} />
            <Route path="/customise/bolster-cushion" element={<BolsterCushionPage />} />
            <Route path="/customise/outdoor-rectangle-bench-seat-cushion" element={<OutdoorBenchSeatPage />} />
            <Route path="/customise/hinged-chair-cushion" element={<HingedCushionPage />} />
            <Route path="/customise/:cushionId" element={<CustomizerPage />} />
            <Route path="/cart" element={<CartPage />} />
            <Route path="/checkout" element={<CheckoutPage />} />
            <Route path="/checkout/success" element={<CheckoutSuccessPage />} />
            <Route path="/fabric-samples" element={<FabricSamplesPage />} />
            <Route path="/our-foams" element={<OurFoamsPage />} />
            <Route path="/faqs" element={<FAQPage />} />
            <Route path="/trade" element={<TradePage />} />
            <Route path="/login" element={<LoginPage />} />
            <Route path="/account" element={<AccountPage />} />
            <Route path="/track-order" element={<OrderTrackingPage />} />
            <Route path="/order/:orderId" element={<OrderDetailPage />} />
            <Route path="/admin" element={<AdminPage />} />
            <Route path="/admin/scraper" element={<AdminPage />} />
            <Route path="/forgot-password" element={<ForgotPasswordPage />} />
            <Route path="/reset-password" element={<ResetPasswordPage />} />
            <Route path="/auth/callback" element={<AuthCallback />} />
            <Route path="/auth/google" element={<GoogleAuthCallback />} />
          </Routes>
        </Layout>
        <Toaster position="top-right" toastOptions={{
          classNames: {
            success: '!bg-teal-600 !text-white !border-teal-600',
            error: '!bg-red-500 !text-white !border-red-500',
          },
        }} />
      </BrowserRouter>
    </CartProvider>
    </SiteGate>
    </AuthProvider>
  );
}

export default App;
